import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import Button from '../components/common/Button'
import Spinner from '../components/common/Spinner'
import i18n from '../i18n'
import { daysAgo, filterAttempts, filterOptions, seedOf, statusChip, summarize, waveformBars } from '../lib/archive'
import { clock } from '../lib/clock'
import { faDigits } from '../lib/numerals'
import { bandOf } from '../lib/scoreBand'
import { classifyFailure } from '../services/api'
import { listHistory } from '../services/lessons'
import { useArchivePlayer } from '../hooks/useArchivePlayer'

/** The design's strip: 34 bars, 26px tall, under every row. */
const WAVE_BARS = 34

const TILE = {
  good: 'bg-good-tint text-good',
  warn: 'bg-warn-tint text-warn',
  bad: 'bg-bad-tint text-bad',
  failed: 'bg-bad-tint text-bad',
  // A queued take (plan B, V9-w) is waiting for a score, not missing one:
  // the tile takes the brand tint rather than the grey of a row with
  // nothing to show. Its recording plays like any other — the upload is
  // what was stored, and the queue is only about the assessment.
  queued: 'bg-brand-50 text-brand-700',
  none: 'bg-surface-2 text-ink-3',
}

/** Plain (non-component) helpers: translated fresh on every call via the direct i18n import. */
const t = (key, options) => i18n.t(key, { ns: 'archive', ...options })

function playerErrorText(code) {
  const map = {
    missing: t('playerError.missing'),
    failed: t('playerError.failed'),
  }
  return map[code] ?? ''
}

function whenLabel(isoInstant) {
  const days = daysAgo(isoInstant)
  if (days === null) return ''
  if (days === 0) return t('when.today')
  if (days === 1) return t('when.yesterday')
  return t('when.daysAgo', { days: faDigits(days) })
}

function PlayGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" className="ms-0.5 fill-current">
      <path d="M8 5.2v13.6L19 12z" />
    </svg>
  )
}

function PauseGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" className="fill-current">
      <rect x="6" y="4" width="4.4" height="16" rx="1.4" />
      <rect x="13.6" y="4" width="4.4" height="16" rx="1.4" />
    </svg>
  )
}

function WaveGlyph() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" className="fill-current">
      {[4, 8, 12, 16, 20].map((x, i) => {
        const h = [8, 14, 18, 12, 6][i]
        return <rect key={x} x={x - 1} y={12 - h / 2} width="2.2" height={h} rx="1.1" />
      })}
    </svg>
  )
}

/**
 * The 34-bar strip under a row. Bars up to the playhead take the row's
 * colour; heights are seeded from the id, not measured (lib/archive.js).
 */
function WaveStrip({ id, progress, playedClass }) {
  const bars = useMemo(() => waveformBars(WAVE_BARS, seedOf(id), 20), [id])
  const max = Math.max(...bars)
  return (
    <div dir="ltr" aria-hidden="true" className="mt-3 flex h-[26px] items-end gap-[2px]">
      {bars.map((bar, index) => {
        const played = progress > 0 && index / WAVE_BARS <= progress
        return (
          <span
            key={index}
            className={`flex-1 rounded-[2px] ${played ? playedClass : 'bg-wave-off'}`}
            style={{ height: `${(bar / max) * 26}px` }}
          />
        )
      })}
    </div>
  )
}

function StatTile({ value, label }) {
  return (
    <div className="flex flex-1 flex-col items-center gap-1 rounded-2xl bg-surface px-2 py-3.5 ring-1 ring-line">
      <span className="text-[19px] font-bold text-ink">{value}</span>
      <span className="text-center text-[10.5px] text-ink-2">{label}</span>
    </div>
  )
}

/**
 * One row of the archive. Exported so the statuses that only a live server
 * produces — a queued take (V9-w) among them — can be rendered in the page
 * test with the item passed as a prop, the way the rest of this repo's
 * page coverage works.
 */
export function ArchiveRow({ item, player, onOpenAnalysis }) {
  const { t } = useTranslation('archive')
  const band = item.overall === null || item.overall === undefined ? null : bandOf(item.overall)
  // Without a score the tile takes its colour from the status, for the two
  // statuses that have one of their own: a failed take and a queued one.
  const tileKey = band ?? (['failed', 'queued'].includes(item.status) ? item.status : 'none')
  const owns = player.playingId === item.id
  const playing = owns && player.isPlaying
  const loading = player.loadingId === item.id
  const playedClass = band ? { good: 'bg-good', warn: 'bg-warn', bad: 'bg-bad' }[band] : 'bg-brand-600'
  const status = statusChip(item.status)

  return (
    <li className="rounded-2xl bg-surface p-3.5 ring-1 ring-line">
      <div className="flex items-center gap-3">
        {/* The tile is the play button, as the design draws it; a row with
            nothing to play keeps the wave glyph and says so on tap. */}
        <button
          type="button"
          onClick={() => player.toggle(item)}
          aria-label={playing ? t('row.pause') : t('row.play')}
          aria-busy={loading || undefined}
          className={`grid size-11 shrink-0 place-items-center rounded-[13px] transition-opacity ${TILE[tileKey]} ${
            loading ? 'opacity-60' : ''
          }`}
        >
          {playing ? <PauseGlyph /> : item.userAudioUrl ? <PlayGlyph /> : <WaveGlyph />}
        </button>

        <div className="min-w-0 flex-1">
          <p className="lesson-text ltr truncate text-[15px] text-ink">{item.lessonTitleEn}</p>
          <p className="mt-0.5 flex items-center gap-2 text-[11.5px] text-ink-2">
            <span>
              {t('row.sentenceLine', { index: faDigits(item.sentenceIndex + 1), when: whenLabel(item.createdAt) })}
            </span>
            {item.audioDurationSeconds ? (
              // Latin digits: a duration, not a count (design-system.md).
              <span className="ltr tnum text-[11px] text-ink-3">{clock(item.audioDurationSeconds)}</span>
            ) : null}
          </p>
        </div>

        {band ? (
          <div className="flex shrink-0 flex-col items-center">
            <span className={`text-lg font-bold ${{ good: 'text-good', warn: 'text-warn', bad: 'text-bad' }[band]}`}>
              {faDigits(item.overall)}
            </span>
            <span className="text-[9.5px] text-ink-3">{t('row.score')}</span>
          </div>
        ) : (
          <span className={`shrink-0 rounded-lg px-2.5 py-1.5 text-[10.5px] font-semibold ${status.className}`}>
            {status.label}
          </span>
        )}
      </div>

      <WaveStrip id={item.id} progress={owns ? player.progress : 0} playedClass={playedClass} />

      <button
        type="button"
        onClick={() => onOpenAnalysis(item)}
        className="mt-3 h-[38px] w-full rounded-[10px] bg-surface-2 text-[12.5px] font-semibold text-ink-2 transition-colors hover:bg-surface-3"
      >
        {t('row.fullAnalysis')}
      </button>
    </li>
  )
}

/**
 * The learner's history (design key `archive`, P-archive of the go-live
 * plan). Data from `attempts/list` — newest first, at most 50, the
 * server's cap. Every row plays its take (B1) and reopens its analysis
 * (B2) through the existing result components. «ارسال به فید» is not
 * drawn: there is no feed (B6).
 *
 * Until the list answers the page shows a spinner, never the empty state —
 * a learner with fifty takes must not be told they have never practised.
 */
export default function Archive() {
  const { t } = useTranslation('archive')
  const navigate = useNavigate()
  const player = useArchivePlayer()
  const [items, setItems] = useState(null)
  const [error, setError] = useState(null)
  const [filter, setFilter] = useState('all')
  const [reload, setReload] = useState(0)

  useEffect(() => {
    let cancelled = false
    setError(null)
    listHistory()
      .then((data) => {
        if (cancelled) return
        // The list is keyed by id; a duplicate from the server would render twice.
        const seen = new Set()
        setItems(data.items.filter((item) => !seen.has(item.id) && seen.add(item.id)))
      })
      .catch((e) => {
        if (!cancelled) setError(e)
      })
    return () => {
      cancelled = true
    }
  }, [reload])

  const visible = useMemo(() => filterAttempts(items ?? [], filter), [items, filter])
  const summary = useMemo(() => summarize(items ?? []), [items])

  if (error && !items) {
    const offline = classifyFailure(error) === 'offline'
    return (
      <div className="space-y-3">
        <ArchiveHeader count={null} />
        <div
          role="status"
          className={`rounded-xl p-3 text-sm ${offline ? 'bg-warn-tint text-warn-ink' : 'bg-bad-tint text-bad-ink'}`}
        >
          {offline
            ? t('list.offlineError')
            : t('list.serverError')}
        </div>
        <Button variant="secondary" className="w-full" onClick={() => setReload((n) => n + 1)}>
          {t('list.retry')}
        </Button>
      </div>
    )
  }

  if (!items) {
    return (
      <div className="space-y-3">
        <ArchiveHeader count={null} />
        <Spinner label={t('list.loading')} />
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div className="space-y-3">
        <ArchiveHeader count={null} />
        <div className="flex flex-col items-center gap-4 rounded-2xl bg-surface px-6 py-12 text-center ring-1 ring-line">
          <p className="text-sm font-semibold text-ink">{t('list.emptyTitle')}</p>
          <p className="text-xs leading-relaxed text-ink-2">
            {t('list.emptyBody')}
          </p>
          <Link to="/dashboard">
            <Button>{t('list.emptyCta')}</Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <ArchiveHeader count={items.length} />

      <div className="flex gap-2">
        <StatTile value={summary.bestScore === null ? '—' : faDigits(summary.bestScore)} label={t('stats.best')} />
        <StatTile value={summary.averageScore === null ? '—' : faDigits(summary.averageScore)} label={t('stats.average')} />
        <StatTile value={faDigits(summary.topCount)} label={t('stats.top')} />
      </div>

      <div className="flex gap-2">
        {filterOptions().map((option) => (
          <button
            key={option.key}
            type="button"
            aria-pressed={filter === option.key}
            onClick={() => setFilter(option.key)}
            className={`h-[34px] flex-1 rounded-full text-xs font-semibold transition-colors ${
              filter === option.key ? 'bg-brand-600 text-white dark:text-slate-900' : 'bg-surface text-ink-2 ring-1 ring-line'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {player.error && (
        <div role="status" className="flex items-center justify-between gap-3 rounded-xl bg-warn-tint px-3 py-2 text-xs text-warn-ink">
          <span>{playerErrorText(player.error)}</span>
          <button type="button" onClick={player.clearError} className="font-semibold">
            {t('list.ok')}
          </button>
        </div>
      )}

      {visible.length === 0 ? (
        <p className="py-6 text-center text-sm text-ink-2">{t('list.filteredEmpty')}</p>
      ) : (
        <ul className="space-y-2.5">
          {visible.map((item) => (
            <ArchiveRow key={item.id} item={item} player={player} onOpenAnalysis={(row) => navigate(`/archive/${row.id}`)} />
          ))}
        </ul>
      )}
    </div>
  )
}

function ArchiveHeader({ count }) {
  const { t } = useTranslation('archive')
  return (
    <header className="flex items-center gap-3">
      <Link to="/profile" aria-label={t('header.back')} className="grid size-10 place-items-center rounded-xl text-ink hover:bg-surface-2">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M9 6l6 6-6 6" />
        </svg>
      </Link>
      <h1 className="flex-1 text-lg font-bold text-ink">{t('header.title')}</h1>
      {count !== null && (
        <span className="rounded-full bg-surface-2 px-3 py-1.5 text-xs font-semibold text-ink-2">{t('header.count', { count: faDigits(count) })}</span>
      )}
    </header>
  )
}
